/**
 * Impressum, Datenschutzerklärung, AGB and AVV (roadmap LEG-1). The texts are German only and live
 * in `legal-texts.tsx`; the operator details come from `lib/legal/operator.ts`. Public pages,
 * linked from the app's footer, from sign-up and from the website.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { DpaText, ImprintText, PrivacyText, TermsText } from "./legal-texts";
import { LEGAL_PAGES } from "@/lib/legal/operator";
import { cn } from "@/lib/utils/cn";

type LegalKey = (typeof LEGAL_PAGES)[number]["key"];

const TEXTS: Record<LegalKey, () => React.ReactNode> = {
  imprint: ImprintText,
  privacy: PrivacyText,
  terms: TermsText,
  dpa: DpaText,
};

export function LegalPage({ page }: { page: LegalKey }) {
  const { t, i18n } = useTranslation();
  const Text = TEXTS[page];
  return (
    <article className="mx-auto max-w-3xl space-y-6" data-testid={`legal-${page}`}>
      <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label={t("legal.nav")}>
        {LEGAL_PAGES.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            aria-current={item.key === page ? "page" : undefined}
            className={cn(
              "text-muted-foreground hover:text-foreground",
              item.key === page && "font-medium text-foreground",
            )}
          >
            {t(`legal.${item.key}`)}
          </Link>
        ))}
      </nav>
      <h1 className="text-3xl font-semibold tracking-tight">{t(`legal.${page}`)}</h1>
      {!i18n.language.startsWith("de") && (
        <p className="text-sm text-muted-foreground">{t("legal.germanOnly")}</p>
      )}
      <div className="space-y-4 text-sm leading-relaxed" lang="de">
        <Text />
      </div>
    </article>
  );
}
